/* eslint-disable no-throw-literal */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import wallet from 'services/api/wallet';
import useTranslation from 'utils/hooks/useTranslation';
import notify, { notifyErrorWithWorkflowId } from 'utils/notificationUtils';
import useAmplitude from 'utils/hooks/useAmplitude';
import { ddLogWithContext } from 'utils/errorLoggerUtil';
import {
  extractScaChallenge,
  getAnalyticsErrorReason,
  getCardLoadErrorCode,
  getCardLoadErrorReason,
  getCardLoadErrorKey,
  getClientBrowserDetails,
  isSameScaChallenge,
  isTransientHttpError,
  normalizeCardLoadStatus,
  toAnalyticsCurrency,
  toMajorUnits,
} from 'utils/walletUtil';
import { getCurrencyCode } from 'utils/currencyUtil';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import { CARD_LOAD_POLL_RESULT, CARD_LOAD_POLL_RETRIES, CARD_LOAD_STATUS } from 'constants/wallet';
import { getPollRetryDelay } from 'constants/paymentsSpa';
import userActions from 'redux/user/actions';
import { walletQueryKeys } from './queryKeys';

const { getWalletQuery, pollCardLoadQuery } = walletQueryKeys;
const {
  errorReason: ERROR_REASONS,
  paymentMethodType: PAYMENT_METHOD_TYPES,
} = AMPLITUDE_WALLET_PROPERTY_VALUES;

export const useGetWallet = ({
  corporateClientId,
  pasAccountId,
  pageNumber,
  pageSize,
  transactionType,
  status,
  orderBy,
}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();

  const getWallet = async () => {
    // No pas-account id means this corporate client has no wallet — same "no wallet" data as a 404.
    if (!pasAccountId) {
      return null;
    }

    try {
      const result = await wallet.read({ pageNumber, pageSize, transactionType, status, orderBy });
      // Axios body is the Wallet Service envelope: { data: WalletDetails }
      return result?.data?.data;
    } catch (error) {
      // 404 means "this corporate client has no wallet" — it is data, not a failure.
      // Only `null` carries that meaning; anything else (including undefined) is left alone.
      if (error?.response?.status === 404) {
        ddLogWithContext({
          type: 'warn',
          message: 'Wallet not found for corporate client',
          context: { corporateClientId },
        });
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: [
      getWalletQuery,
      { corporateClientId, pasAccountId, pageNumber, pageSize, transactionType, status, orderBy },
    ],
    queryFn: getWallet,
    enabled: !!corporateClientId,
    // The app-wide QueryClient retries 3x; a failing wallet read should toast promptly instead.
    retry: false,
    // Deliberate deviation from the repo default (false): summary and history share one endpoint,
    // so paging the history must not unmount the whole dashboard behind the page spinner.
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // Always re-read on mount so returning from /wallet/create shows the new wallet.
    staleTime: 0,
    onError: () => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wallet.dashboard.error.fetch'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletFetchFailed);
    },
  });
};

// `currency` is the wallet's own currency, for analytics only.
export const useAddWireLoad = ({ currency } = {}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, ...data }) => {
      const result = await wallet.wireLoad(data);
      // Axios body is the Wallet Service envelope: { data: { wireTransfer, proofUpload } }
      const { wireTransfer, proofUpload } = result?.data?.data ?? {};

      if (proofUpload) {
        ddLogWithContext({
          type: 'info',
          message: 'Uploading wire-load proof to presigned url',
          context: { wireTransferId: wireTransfer?.wireTransferId, proofId: proofUpload.proofId },
        });
        await wallet.uploadWireLoadProof({
          uploadUrl: proofUpload.uploadUrl,
          headers: proofUpload.requiredHeaders,
          file,
        });
      } else {
        ddLogWithContext({
          type: 'warn',
          message: 'Wire-load response had no proofUpload — proof was not uploaded',
          context: { wireTransferId: wireTransfer?.wireTransferId },
        });
      }

      return wireTransfer;
    },
    onSuccess: (wireTransfer, variables) => {
      // The wire load lands on the wallet as a pending transaction — re-read the wallet
      // so the dashboard balance and transaction history reflect it without a reload.
      queryClient.invalidateQueries(getWalletQuery, { exact: false });
      notify({
        type: 'success',
        message: translateText(
          'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.success',
        ),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletWireLoadSuccessful, {
        amount: toMajorUnits(variables?.declaredAmountMinor),
        currency: toAnalyticsCurrency(currency),
        paymentMethodType: PAYMENT_METHOD_TYPES.bankTransfer,
        requestId: wireTransfer?.wireTransferId ?? null,
      });
    },
    onError: error => {
      notify({
        type: 'error',
        message: translateText(
          'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.error',
        ),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletWireLoadFailed, {
        paymentMethodType: PAYMENT_METHOD_TYPES.bankTransfer,
        errorReason: getAnalyticsErrorReason(error, ERROR_REASONS.bankTransferFailed),
      });
    },
  });
};

// Polls GET .../card-load-operations/{operationId} until the load is credited or failed. Same
// mechanism as usePollPayments (PAS card creation): react-query's retry loop is the poller —
// non-terminal reads throw a `pending` literal so `retry` keeps going, and the query is only ever
// started explicitly through `refetch()`.
//
// `attempt` numbers each Confirm; `isCurrentAttempt` says whether that attempt is still the live
// one. Removing the query cancels sleeping retries but not a GET already in flight, so its result
// must not touch the UI once the attempt has been abandoned.
const usePollCardLoad = ({ operationId, workflowId, attempt, isCurrentAttempt }) => {
  const [sca, setSca] = useState(null);

  const pollCardLoad = useCallback(
    async ({ queryKey }) => {
      const [, id, traceId, pollAttempt] = queryKey;
      const result = await wallet.readCardLoad({ operationId: id, workflowId: traceId });
      // Axios body is the Wallet Service envelope: { data: CardLoadOperationResponse }
      const operation = result?.data?.data ?? {};
      const status = normalizeCardLoadStatus(operation.loadStatus);
      const context = { operationId: id, workflowId: traceId, loadStatus: operation.loadStatus };
      const updateSca = updater => {
        if (isCurrentAttempt(pollAttempt)) {
          setSca(updater);
        }
      };

      if (!operation.operationId) {
        // A read that resolved without an operation (httpWrapper swallows a 401 into a sign-out
        // redirect, for one) can never reach a terminal status — stop instead of polling on.
        ddLogWithContext({
          type: 'warn',
          message: 'Card load read resolved without an operation body. Exiting polling process.',
          context,
        });
        throw { result: CARD_LOAD_POLL_RESULT.unreadable, operationId: id, workflowId: traceId };
      }

      if (status === CARD_LOAD_STATUS.actionRequired) {
        let challenge;
        try {
          challenge = extractScaChallenge(operation.sca);
        } catch (decodeError) {
          // A challenge we cannot render can never be completed — fail now rather than poll on
          ddLogWithContext({
            type: 'error',
            message: `Card load SCA challenge could not be decoded: ${decodeError.message}`,
            context,
          });
          throw {
            result: CARD_LOAD_POLL_RESULT.failed,
            failure: { code: decodeError.code, message: decodeError.message },
            operationId: id,
            workflowId: traceId,
          };
        }
        if (challenge) {
          updateSca(previous => (isSameScaChallenge(previous, challenge) ? previous : challenge));
        } else {
          ddLogWithContext({
            type: 'warn',
            message: 'Card load requires action but carries no SCA challenge to render',
            context,
          });
        }
      } else {
        // A spent challenge is cleared server-side once the load moves on; drop ours with it.
        updateSca(previous => (previous ? null : previous));
      }

      if (status === CARD_LOAD_STATUS.credited) {
        ddLogWithContext({ type: 'info', message: 'Card load credited', context });
        return operation;
      }

      if (status === CARD_LOAD_STATUS.failed || (status === null && operation.failure)) {
        ddLogWithContext({
          type: 'warn',
          message: `Card load failed (${operation.failure?.code ?? 'no code'}): ${operation.failure
            ?.message ?? ''}`,
          context,
        });
        throw {
          result: CARD_LOAD_POLL_RESULT.failed,
          failure: operation.failure ?? null,
          operationId: id,
          workflowId: traceId,
        };
      }

      // processing, actionrequired, or a status this client doesn't know yet — keep polling
      throw { result: CARD_LOAD_POLL_RESULT.pending, status, operationId: id, workflowId: traceId };
    },
    [isCurrentAttempt],
  );

  const query = useQuery({
    queryKey: [pollCardLoadQuery, operationId, workflowId, attempt],
    queryFn: pollCardLoad,
    enabled: false,
    retry: (retryCount, error) => {
      if (retryCount > CARD_LOAD_POLL_RETRIES) {
        ddLogWithContext({
          type: 'warn',
          message: 'Card load polling expired before a terminal status',
          context: { operationId: error?.operationId, workflowId: error?.workflowId },
        });
        return false;
      }
      if (error?.result === CARD_LOAD_POLL_RESULT.pending) {
        return true;
      }
      if (
        error?.result === CARD_LOAD_POLL_RESULT.failed ||
        error?.result === CARD_LOAD_POLL_RESULT.unreadable
      ) {
        return false;
      }
      return isTransientHttpError(error);
    },
    retryDelay: getPollRetryDelay,
  });

  const pollReset = useCallback(() => {
    // Removing the query cancels a retry that is still sleeping, so no further GET goes out.
    query.remove();
    setSca(null);
  }, [query]);

  return {
    startPolling: query.refetch,
    isPolling: query.isLoading,
    isPollSuccess: query.isSuccess,
    isPollError: query.isError,
    pollError: query.error,
    sca,
    pollReset,
  };
};

// Card top-up: POST card-load-operations, then poll the returned operation until it is credited
// or failed, surfacing any SCA/3DS challenge along the way. `sca` is what the challenge modal
// renders ({ type, challengeHtml, redirectUrl } or null); `reset` abandons the current attempt.
// `currency` is the wallet's own currency, for analytics only.
export const useAddCardLoad = ({ onReset, currency } = {}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const queryClient = useQueryClient();
  const [pollingDetails, setPollingDetails] = useState(null);
  // Every Confirm is a numbered attempt and reset() retires the live one. react-query's
  // useMutation still runs its onSuccess for a request that resolves after reset, and a poll GET
  // already in flight still resolves after the query is removed; both check the attempt first.
  const attemptRef = useRef(0);
  const isCurrentAttempt = useCallback(attempt => attempt === attemptRef.current, []);
  const {
    startPolling,
    isPolling,
    isPollSuccess,
    isPollError,
    pollError,
    sca,
    pollReset,
  } = usePollCardLoad({ ...(pollingDetails ?? {}), isCurrentAttempt });

  useEffect(() => {
    if (pollingDetails?.operationId) {
      ddLogWithContext({
        type: 'info',
        message: 'Card load created. Starting polling process.',
        context: pollingDetails,
      });
      startPolling();
    }
  }, [pollingDetails, startPolling]);

  const mutation = useMutation({
    mutationKey: 'createCardLoad',
    mutationFn: async ({ walletId, amountMinor, paymentAccountId, userAccountId, attempt }) => {
      // One trace id for the create and every poll that follows it
      const workflowId = uuidv4();
      try {
        const result = await wallet.createCardLoad({
          walletId,
          amountMinor,
          paymentAccountId,
          userAccountId,
          clientBrowserDetails: getClientBrowserDetails(),
          workflowId,
        });
        // Axios body is the Wallet Service envelope: { data: CardLoadOperationResponse }
        const operation = result?.data?.data;
        if (!operation?.operationId) {
          // Without an id there is nothing to poll; surface it instead of silently doing nothing
          throw new Error('Card load response carried no operationId');
        }
        return { operation, workflowId, attempt, amountMinor };
      } catch (error) {
        if (error && typeof error === 'object') {
          error.workflowId = workflowId;
        }
        throw error;
      }
    },
    onSuccess: ({ operation, workflowId, attempt }) => {
      if (!isCurrentAttempt(attempt)) {
        // The user closed the modal while the POST was in flight; the load runs its course on the
        // server, but nothing here should poll or toast for it.
        ddLogWithContext({
          type: 'info',
          message: 'Card load created after the attempt was abandoned. Not polling.',
          context: { operationId: operation.operationId, workflowId },
        });
        return;
      }
      setPollingDetails({ operationId: operation.operationId, workflowId, attempt });
    },
  });

  useEffect(() => {
    if (isPollSuccess) {
      // The balance and history come from GET wallet — re-read so the new top-up shows.
      queryClient.invalidateQueries(getWalletQuery, { exact: false });
      notify({
        type: 'success',
        message: translateText(
          'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.success',
        ),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCardLoadSuccessful, {
        amount: toMajorUnits(mutation.data?.amountMinor),
        currency: toAnalyticsCurrency(currency),
        paymentMethodType: PAYMENT_METHOD_TYPES.card,
        requestId: pollingDetails?.operationId ?? null,
      });
    }
    // Success feedback fires once per credited load; the other values are stable helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPollSuccess]);

  const error = mutation.error ?? pollError ?? null;

  useEffect(() => {
    if (!error) {
      return;
    }
    const errorKey = getCardLoadErrorKey(error);
    const code = getCardLoadErrorCode(error);
    const message = translateText(
      `corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.error.${errorKey}`,
    );

    notifyErrorWithWorkflowId({
      errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
      message: code ? `${message} (${code})` : message,
      workflowId:
        error.workflowId ?? error.response?.data?.workflowId ?? pollingDetails?.workflowId,
    });
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCardLoadFailed, {
      reason: errorKey,
      paymentMethodType: PAYMENT_METHOD_TYPES.card,
      errorReason: getCardLoadErrorReason(errorKey),
    });
    // Error feedback fires once per distinct error object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // react-query keeps these two stable across renders; the `mutation` result object itself is new
  // every render and would make every callback below (and the modals they reach) re-create too.
  const { mutate: mutateCardLoad, reset: resetMutation } = mutation;

  const reset = useCallback(() => {
    // A load abandoned mid-flight may still be credited server-side — make the dashboard re-read.
    // (A credited load already triggered that re-read in the success effect above.)
    const abandonedInFlight = !!pollingDetails && !isPollSuccess;
    attemptRef.current += 1;
    resetMutation();
    pollReset();
    setPollingDetails(null);
    if (onReset) {
      onReset();
    }
    if (abandonedInFlight) {
      queryClient.invalidateQueries(getWalletQuery, { exact: false });
    }
  }, [resetMutation, pollReset, pollingDetails, isPollSuccess, onReset, queryClient]);

  const mutate = useCallback(
    variables => {
      // A fresh attempt gets a fresh poll query (and a fresh idempotency id from the API layer)
      attemptRef.current += 1;
      pollReset();
      setPollingDetails(null);
      mutateCardLoad({ ...variables, attempt: attemptRef.current });
    },
    [mutateCardLoad, pollReset],
  );

  return {
    mutate,
    isSubmitting: mutation.isLoading,
    isPolling,
    isLoading: mutation.isLoading || isPolling,
    isSuccess: isPollSuccess,
    isError: mutation.isError || isPollError,
    error,
    sca,
    reset,
  };
};

// `currency` is the wallet's own currency, for analytics only.
export const useEditWalletLowBalanceAlert = ({ currency } = {}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ walletId, lowBalanceAlertThresholdMinor }) => {
      const result = await wallet.updateSettings({ walletId, lowBalanceAlertThresholdMinor });
      return result?.data?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(getWalletQuery, { exact: false });
      notify({
        type: 'success',
        message: translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.success'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertUpdateSuccessful, {
        amount: toMajorUnits(variables?.lowBalanceAlertThresholdMinor),
        currency: toAnalyticsCurrency(currency),
      });
    },
    onError: error => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.error'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertUpdateFailed, {
        errorReason: getAnalyticsErrorReason(error),
      });
    },
  });
};

// TODO(FS-157/FS-180): once freeze/close hit the real Wallet Service, invalidate
// walletQueryKeys.getWalletQuery in their onSuccess so the dashboard status follows the server.
// `walletBalance` (major units) and `currency` are the wallet at the moment of the action, for
// analytics only.
export const useFreezeWallet = ({ walletBalance, currency } = {}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();

  return useMutation({
    mutationFn: async () => {
      const result = await wallet.freeze();
      return result?.data;
    },
    onSuccess: () => {
      notify({
        type: 'success',
        message: translateText('corporateAccounts.wallet.dashboard.footer.freeze.success'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletFreezeSuccessful, {
        walletBalance,
        currency: toAnalyticsCurrency(currency),
      });
    },
    onError: error => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wallet.dashboard.footer.freeze.error'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletFreezeFailed, {
        errorReason: getAnalyticsErrorReason(error),
        walletBalance,
        currency: toAnalyticsCurrency(currency),
      });
    },
  });
};

export const useCreateWallet = () => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const dispatch = useDispatch();
  // A new wallet is always in the corporation country's currency
  const currency = useSelector(state => getCurrencyCode(state.user.currentCorporation?.country));

  return useMutation({
    mutationFn: async data => {
      const result = await wallet.create(data);
      // Axios body is the Wallet Service envelope: { data: WalletDetails }
      return result?.data?.data;
    },
    onSuccess: (createdWallet, variables) => {
      // The corporation's pas-account id is what useGetWallet keys the dashboard read on
      // (see services/user.js#getWalletPasAccountId) — keep it in sync so the wallet page,
      // once navigated to, immediately reads the wallet that was just created.
      dispatch({
        type: userActions.UPDATE_CURRENT_CORPORATION,
        payload: { walletPaymentAccountIds: [createdWallet?.pasAccountId] },
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCreateSuccessful, {
        currency,
        lowBalanceAlertAmount: toMajorUnits(variables?.lowBalanceAlertThresholdMinor),
      });
    },
    onError: error => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wallet.create.error'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCreateFailed, {
        currency,
        errorReason: getAnalyticsErrorReason(error),
      });
    },
  });
};

// `walletBalance` (major units) and `currency` are the wallet at the moment of the action, for
// analytics only.
export const useCloseWallet = ({ walletBalance, currency } = {}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();

  return useMutation({
    mutationFn: async note => {
      const result = await wallet.close(note);
      return result?.data;
    },
    onSuccess: () => {
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCloseSuccessful, {
        walletBalance,
        currency: toAnalyticsCurrency(currency),
      });
    },
    onError: error => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wallet.dashboard.footer.close.error'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCloseFailed, {
        errorReason: getAnalyticsErrorReason(error),
        walletBalance,
        currency: toAnalyticsCurrency(currency),
      });
    },
  });
};

export default {
  useCreateWallet,
  useGetWallet,
  useAddWireLoad,
  useAddCardLoad,
  useEditWalletLowBalanceAlert,
  useFreezeWallet,
  useCloseWallet,
};
